import { forwardRef, type ComponentPropsWithoutRef } from 'react';
import { cn } from '../lib/cn';
import { formatCurrency } from '../lib/currency';
import { Typography } from './typography';

export type ProgressBarProps = {
  value: number; // 0-100
  label?: string;
  valueOnTop?: boolean;
  lighterProgressColor?: boolean;
  indicator?: number;
  indicatorNegative?: boolean;
  progressBarFlat?: boolean;
  min?: number;
  max?: number;
  currency?: boolean;
  numeralValue?: boolean;
  fraction?: string;
} & ComponentPropsWithoutRef<'div'>;

export const ProgressBar = forwardRef<HTMLDivElement, ProgressBarProps>(
  (
    {
      value,
      valueOnTop = false,
      lighterProgressColor = false,
      progressBarFlat = false,
      label,
      indicator,
      indicatorNegative,
      className,
      min = 0,
      max = 100,
      currency = false,
      numeralValue = false,
      fraction,
      ...rest
    },
    ref
  ) => {
    const percent = ((value - min) / (max - min)) * 100;
    const clamped = Math.max(0, Math.min(100, percent));

    const indicatorPercent =
      indicator != null
        ? Math.max(0, Math.min(100, ((indicator - min) / (max - min)) * 100))
        : null;

    const formattedValue = currency
      ? formatCurrency(indicator ?? clamped)
      : fraction
        ? fraction
        : `${indicator ?? clamped}${!numeralValue ? '%' : ''}`;

    return (
      <div
        role="progressbar"
        ref={ref}
        aria-valuemax={max}
        aria-valuemin={min}
        aria-valuenow={clamped}
        className={cn('flex flex-col gap-2', className)}
        {...rest}
      >
        {(label || valueOnTop) && (
          <div className="flex w-full cursor-default items-center justify-between gap-x-3 select-none">
            {label ? (
              <Typography className="text-neutral-9">{label}</Typography>
            ) : (
              <div />
            )}
            {valueOnTop && (
              <Typography
                as="h4"
                variant="h4"
                weight="medium"
                className="cursor-default select-none"
              >
                {formattedValue}
              </Typography>
            )}
          </div>
        )}

        <div
          className={cn('flex w-full items-center', !valueOnTop && 'flex-row')}
        >
          <div className="bg-neutral-4 relative h-2 w-full rounded-full">
            <div
              style={{ width: `${clamped}%` }}
              className={cn(
                'h-full rounded-full transition-[width] duration-300 ease-out',
                lighterProgressColor ? 'bg-primary-4' : 'bg-primary-6',
                progressBarFlat && 'rounded-r-none'
              )}
            />

            {indicator != null && (
              <div
                className="absolute -top-1 flex -translate-x-1/2 flex-col items-center duration-300 ease-in-out"
                style={{
                  left: `${indicatorPercent}%`,
                }}
              >
                <div
                  className={cn(
                    'border-t-polar-green-6 h-0 w-0 self-center border-t-[8px] border-r-[10px] border-l-[10px] border-r-transparent border-l-transparent',
                    indicatorNegative && 'border-t-dust-red-6'
                  )}
                />
              </div>
            )}
          </div>

          {!valueOnTop && (
            <Typography
              variant="body"
              weight="regular"
              className="ml-2 cursor-default select-none"
            >
              {Math.round(clamped)}%
            </Typography>
          )}
        </div>
      </div>
    );
  }
);
